import { SPEC_DATA_PART_TYPE } from "@json-render/core";
import { useRef, useState } from "react";
import ChatBubble from "@/components/ui/chat/ChatBubble";
import ChatResponseControls from "@/components/ui/chat/ChatResponseControls";
import { toast } from "@/components/ui/toast";
import { useDownloadHtmlAsPdf } from "@/hooks/useDownloadHtmlAsPdf";
import { prepareAssistantHtmlForPdf } from "@/lib/chat/prepareAssistantHtmlForPdf";
import { getAssistantPlainTextForCopy } from "@/lib/utils/aiChatUtils";
import { copyTextToClipboard } from "@/lib/utils/clipboard";
import { formatDateForUrl } from "@/lib/utils/dateUtils";
import { AssistantMessageContent } from "./AssistantMessageContent";

interface AssistantMessageBubbleProps {
  parts: ReadonlyArray<Record<string, unknown> & { type: string }>;
  /** Same flag passed to {@link AssistantMessageContent}: latest assistant while streaming/submitted. */
  isLatestAssistantStreaming: boolean;
  /** Sent with PDF requests so the API can pick `Content-Disposition` (etc.). */
  conversationId?: string;
  /** Persisted `createdAt`; responses streamed in this session fall back to first render time. */
  createdAt?: string;
}

/**
 * Assistant turn: markdown/spec body plus per-response actions once streaming completes.
 */
export const AssistantMessageBubble = ({
  parts,
  isLatestAssistantStreaming,
  conversationId,
  createdAt,
}: AssistantMessageBubbleProps) => {
  const heraldResponseRef = useRef<HTMLDivElement>(null);
  const [firstRenderedAt] = useState(() => new Date());
  const reportDateValue = formatDateForUrl(firstRenderedAt);
  const { download: downloadPdf, isPdfGenerating } = useDownloadHtmlAsPdf();
  const plainForCopy = getAssistantPlainTextForCopy(parts, SPEC_DATA_PART_TYPE);
  const onCopy =
    plainForCopy.length > 0
      ? async () => {
          const ok = await copyTextToClipboard(plainForCopy);
          if (!ok) {
            toast.error("Could not copy to clipboard");
            return false;
          }
          return true;
        }
      : undefined;

  const handleDownloadPdf = () => {
    const root = heraldResponseRef.current;
    if (!root) {
      return;
    }
    const html = prepareAssistantHtmlForPdf(root);
    void downloadPdf(html, {
      conversationId,
    });
  };

  return (
    <ChatBubble showAvatar={false} className="group/chat-turn">
      <AssistantMessageContent
        ref={heraldResponseRef}
        parts={parts}
        isLatestAssistantStreaming={isLatestAssistantStreaming}
        reportDateValue={reportDateValue}
      />
      {!isLatestAssistantStreaming ? (
        <div className="flex justify-start pt-1">
          <ChatResponseControls
            createdAt={createdAt ?? firstRenderedAt}
            isPdfGenerating={isPdfGenerating}
            onCopy={onCopy}
            onDownloadPdf={handleDownloadPdf}
          />
        </div>
      ) : null}
    </ChatBubble>
  );
};
